import { api } from "../../convex/_generated/api";
import {
  runConvexAction,
  runConvexMutation,
} from "./convexClient";

const SESSION_STORAGE_KEY = "highlanderhomes_web_session";
const CLIENT_NAME = "web";

function sanitizeSession(payload) {
  if (!payload?.user || !payload?.token) {
    return null;
  }
  return {
    token: payload.token,
    user: payload.user,
  };
}

export function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return sanitizeSession(JSON.parse(raw));
  } catch (error) {
    console.error("Failed to parse saved session:", error);
    return null;
  }
}

export function saveSession(session) {
  const sanitized = sanitizeSession(session);
  if (!sanitized) {
    return null;
  }
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sanitized));
  return sanitized;
}

export function clearSession() {
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

export function getSessionToken() {
  return loadSession()?.token ?? null;
}

export async function signInWithEmail(email, password) {
  const response = await runConvexAction(api.auth.signIn, {
    email,
    password,
    client: CLIENT_NAME,
  });
  return saveSession(response);
}

export async function signUpWithEmail({ name, email, password }) {
  return runConvexAction(api.auth.signUp, {
    name,
    email,
    password,
    client: CLIENT_NAME,
  });
}

export async function verifyEmailCode({ email, code }) {
  const response = await runConvexAction(api.auth.verifyEmail, {
    email,
    code,
    client: CLIENT_NAME,
  });
  return saveSession(response);
}

export async function resendEmailCode(email) {
  return runConvexAction(api.auth.sendVerificationEmail, { email });
}

export async function requestPasswordReset(email) {
  return runConvexAction(api.auth.resetPassword, { email });
}

export async function confirmPasswordReset({ email, code, newPassword }) {
  const response = await runConvexAction(api.auth.confirmPasswordReset, {
    email,
    code,
    newPassword,
    client: CLIENT_NAME,
  });
  return saveSession(response);
}

export async function changePassword({ email, currentPassword, newPassword }) {
  const response = await runConvexAction(api.auth.changePassword, {
    email,
    currentPassword,
    newPassword,
    client: CLIENT_NAME,
  });
  // Changing the password revokes every session; adopt the fresh one.
  if (response?.token && response?.user) {
    saveSession(response);
  }
  return response;
}

export async function signOutSession() {
  const token = getSessionToken();
  clearSession();
  if (token) {
    try {
      await runConvexMutation(api.sessions.signOut, { token });
    } catch (error) {
      // The local session is already cleared; server cleanup is best-effort.
      console.warn("Server sign-out failed:", error);
    }
  }
}
